import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { AlertTriangle, Trash2, X, Loader2 } from 'lucide-react';

interface DeleteConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: string;
  itemName?: string;
  /** İsteğe bağlı: kullanıcının onaylamak için yazması gereken metin */
  requireText?: string;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  itemName,
  requireText,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [typedText, setTypedText] = useState('');

  if (!isOpen) return null;

  const canConfirm = requireText ? typedText.trim() === requireText : true;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      await onConfirm();
      onClose();
      setTypedText('');
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4" style={{backgroundColor:"rgba(0,0,0,0.8)"}}>
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ type: 'spring', stiffness: 220, damping: 22 }}
        className="dribbble-panel max-w-md w-full overflow-hidden"
      >
        {/* Üst */}
        <div className="relative p-6 pb-4">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Animasyonlu uyarı ikonu */}
          <div className="relative w-16 h-16 mb-4">
            <span className="absolute inset-0 bg-red-100 rounded-2xl animate-ping opacity-50" />
            <div className="relative w-16 h-16 bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl flex items-center justify-center shadow-lg shadow-red-300/40">
              <AlertTriangle className="w-8 h-8 text-white" strokeWidth={2.2} />
            </div>
          </div>

          <h2 className="text-xl font-bold text-gray-900 mb-2">{title}</h2>
          <p className="text-sm text-gray-600 leading-relaxed">
            {message}
            {itemName && (
              <> <strong className="font-bold text-gray-900">"{itemName}"</strong></>
            )}
          </p>

          {/* Uyarı kutusu */}
          <div className="mt-4 p-3 rounded-xl bg-red-50 border border-red-200">
            <p className="text-xs text-red-700 font-semibold flex items-center gap-1.5">
              ⚠️ Bu işlem <span className="underline">geri alınamaz</span>.
            </p>
          </div>

          {/* Yazma onayı (opsiyonel) */}
          {requireText && (
            <div className="mt-4 space-y-1.5">
              <label className="block text-xs font-semibold text-gray-700">
                Onaylamak için <code className="px-1.5 py-0.5 rounded bg-gray-100 text-red-600 font-bold">{requireText}</code> yazın:
              </label>
              <input
                type="text"
                value={typedText}
                onChange={(e) => setTypedText(e.target.value)}
                placeholder={requireText}
                className="w-full rounded-xl border-2 border-gray-200 focus:border-red-400 focus:ring-4 focus:ring-red-500/20 outline-none px-3 py-2 text-sm bg-white"
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-gray-200 bg-gray-50/50">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary flex-1"
            disabled={submitting}
          >
            İptal
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting || !canConfirm}
            className="btn btn-danger flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Siliniyor...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                Evet, Sil
              </>
            )}
          </button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

export default DeleteConfirmModal;
